/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useUser } from '#hooks/UNSAFE_useUser';
import { useCallback } from 'preact/compat';

type SupportedKeyBindings =
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'ArrowUp'
  | 'ArrowDown'
  | 'End'
  | 'Home'
  | 'PageUp'
  | 'PageDown'
  | 'ShiftPageUp'
  | 'ShiftPageDown'
  | 'CtrlAltT';
type NavigationValue<T extends string> = T | { ltr: T; rtl: T };
type NavigationMap<T extends string> = Partial<Record<SupportedKeyBindings, NavigationValue<T>>>;

type UseCellNavigationProps<Payload extends object, Navigation extends string> = {
  /**
   * Payload for the callbacks from this cell.
   */
  payload: Payload;

  /**
   * An object that maps the key codes to the direction of navigation. The onNavigation
   * callback will be invoked only for the key codes provided in the map and specifies the
   * direction as provided in the value.
   */
  navigationMap: NavigationMap<Navigation>;

  /**
   * A callback that will be invoked when a selection action is performed on the DatePicker cell.
   * @param payload The provided payload.
   */
  onAction: (payload: Payload) => void;

  /**
   * A callback that will be invoked when a navigation action is performed on the DatePicker cell.
   * @param details The object specifying the direction of the navigation performed and the payload.
   */
  onNavigation: (details: { direction: Navigation } & Payload) => void;
};

/**
 * A custom hook that handles navigation for a DatePicker cell.
 */
export const useCellNavigation = <T extends object, U extends string>({
  navigationMap,
  payload,
  onAction,
  onNavigation
}: UseCellNavigationProps<T, U>) => {
  const { direction } = useUser();
  const handleSelection = useCallback(() => {
    onAction(payload);
  }, [payload, onAction]);

  const handleKeyDown = useCallback<NonNullable<GlobalEventHandlers['onkeydown']>>(
    (event) => {
      const killEvent = () => {
        event.preventDefault();
        event.stopPropagation();
      };

      if (['Enter', 'Space'].includes(event.code)) {
        // since we are handling the event, we need to kill the event here
        // so that no other action is performed
        killEvent();
        handleSelection();
        return;
      }

      // Handle Ctrl + Alt + T
      if (event.code === 'KeyT' && event.ctrlKey && event.altKey) {
        const navigationDirection = navigationMap['CtrlAltT'];
        if (navigationDirection !== undefined) {
          // since we are handling the event, we need to kill the event here
          // so that no other action is performed
          killEvent();
          onNavigation({
            direction: getLogicalValue(navigationDirection, direction),
            ...payload
          });
        }
      }

      const { key, shiftKey } = event;

      switch (key) {
        // since we are handling the event, we need to kill the event here
        // so that no other action is performed
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'ArrowUp':
        case 'ArrowDown':
        case 'End':
        case 'Home':
        case 'PageUp':
        case 'PageDown':
          const mapKey: SupportedKeyBindings =
            shiftKey && (key === 'PageUp' || key === 'PageDown') ? `Shift${key}` : key;
          if (navigationMap[mapKey] !== undefined) {
            killEvent();
            const navigationDirection = navigationMap[mapKey]!;
            onNavigation({
              direction: getLogicalValue(navigationDirection, direction),
              ...payload
            });
          }
      }
    },
    [direction, handleSelection, navigationMap, onNavigation, payload]
  );

  return {
    onClick: handleSelection,
    onKeyDown: handleKeyDown
  };
};

/**
 * Determines the logical value for the navigation direction.
 * @param value The value representing the navigation direction
 * @param dir The reading direction from the user preferences
 * @returns The logical navigation direction
 */
const getLogicalValue = <T extends string>(value: NavigationValue<T>, dir: 'ltr' | 'rtl') =>
  typeof value === 'string' ? value : value[dir];
